/**
 * Logo Carousel Module
 * Handles horizontal scrolling carousel for logo cards on smaller screens
 */

const ERROR_MESSAGES = {
	CONTAINER_NOT_FOUND: 'Logo Carousel: Container not found',
	ELEMENTS_NOT_FOUND: 'Logo Carousel: Required elements not found',
	INIT_FAILED: 'Logo Carousel: Initialization failed',
	SCROLL_FAILED: 'Logo Carousel: Scroll operation failed',
	RESIZE_FAILED: 'Logo Carousel: Resize handling failed',
};

const CONFIG = {
	SELECTORS: {
		CONTAINER: '.logo-carousel-container',
		LOGO_CARDS: '.logo-cards',
		PREV_BUTTON: '.logo-carousel-prev',
		NEXT_BUTTON: '.logo-carousel-next',
		LOGO_CARD: '.logo-card',
	},
	BREAKPOINT: 1025, // tablet-mobile breakpoint
};

/**
 * Initialize logo carousel functionality
 */
const initializeLogoCarousel = () => {
	try {
		const carouselContainer = document.querySelector(CONFIG.SELECTORS.CONTAINER);

		if (!carouselContainer) {
			console.warn(ERROR_MESSAGES.CONTAINER_NOT_FOUND);
			return;
		}

		const logoCards = carouselContainer.querySelector(CONFIG.SELECTORS.LOGO_CARDS);
		const prevButton = carouselContainer.querySelector(CONFIG.SELECTORS.PREV_BUTTON);
		const nextButton = carouselContainer.querySelector(CONFIG.SELECTORS.NEXT_BUTTON);

		if (!logoCards || !prevButton || !nextButton) {
			console.warn(ERROR_MESSAGES.ELEMENTS_NOT_FOUND);
			return;
		}

		let listenersAttached = false;

		const updateButtonStates = () => {
			try {
				const tabButtons = Array.from(logoCards.querySelectorAll('.nav-link'));
				const activeIndex = tabButtons.findIndex((button) => button.classList.contains('active'));

				prevButton.disabled = activeIndex <= 0;
				nextButton.disabled = activeIndex >= tabButtons.length - 1;
			} catch (error) {
				console.error(ERROR_MESSAGES.SCROLL_FAILED, error);
			}
		};

		const handleTabShown = (e) => {
			const newlyActiveTabButton = e.target;
			const logoCard = newlyActiveTabButton.closest(CONFIG.SELECTORS.LOGO_CARD);
			if (logoCard) {
				logoCard.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
			}
			updateButtonStates();
		};

		const handlePrevClick = () => {
			try {
				const tabButtons = Array.from(logoCards.querySelectorAll('.nav-link'));
				const activeIndex = tabButtons.findIndex((button) => button.classList.contains('active'));
				const prevIndex = activeIndex - 1;
				if (prevIndex >= 0) {
					tabButtons[prevIndex].click();
				}
			} catch (error) {
				console.error(ERROR_MESSAGES.SCROLL_FAILED, error);
			}
		};

		const handleNextClick = () => {
			try {
				const tabButtons = Array.from(logoCards.querySelectorAll('.nav-link'));
				const activeIndex = tabButtons.findIndex((button) => button.classList.contains('active'));
				const nextIndex = activeIndex + 1;
				if (nextIndex < tabButtons.length) {
					tabButtons[nextIndex].click();
				}
			} catch (error) {
				console.error(ERROR_MESSAGES.SCROLL_FAILED, error);
			}
		};

		const handleKeyDown = (e) => {
			try {
				if (window.innerWidth <= CONFIG.BREAKPOINT) {
					if (e.key === 'ArrowLeft') {
						e.preventDefault();
						handlePrevClick();
					} else if (e.key === 'ArrowRight') {
						e.preventDefault();
						handleNextClick();
					}
				}
			} catch (error) {
				console.error(ERROR_MESSAGES.SCROLL_FAILED, error);
			}
		};

		const checkCarouselNeeded = () => {
			try {
				const isSmallScreen = window.innerWidth <= CONFIG.BREAKPOINT;

				if (isSmallScreen) {
					if (!listenersAttached) {
						prevButton.addEventListener('click', handlePrevClick);
						nextButton.addEventListener('click', handleNextClick);
						const tabButtons = logoCards.querySelectorAll('.nav-link');
						tabButtons.forEach((button) => {
							button.addEventListener('shown.bs.tab', handleTabShown);
						});
						listenersAttached = true;
					}
					updateButtonStates();
				} else {
					if (listenersAttached) {
						prevButton.removeEventListener('click', handlePrevClick);
						nextButton.removeEventListener('click', handleNextClick);
						const tabButtons = logoCards.querySelectorAll('.nav-link');
						tabButtons.forEach((button) => {
							button.removeEventListener('shown.bs.tab', handleTabShown);
						});
						listenersAttached = false;
					}
					logoCards.scrollLeft = 0;
				}
			} catch (error) {
				console.error(ERROR_MESSAGES.RESIZE_FAILED, error);
			}
		};

		const setupCarousel = () => {
			checkCarouselNeeded();
			window.addEventListener('resize', checkCarouselNeeded);
			logoCards.addEventListener('keydown', handleKeyDown);
		};

		if (document.readyState === 'complete') {
			setupCarousel();
		} else {
			window.addEventListener('load', setupCarousel);
		}
	} catch (error) {
		console.error(ERROR_MESSAGES.INIT_FAILED, error);
	}
};

export { initializeLogoCarousel };
